import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

// Futuristic 3D Energy Orb Component
const EnergyOrb = () => {
  return (
    <div className="relative w-[280px] h-[280px] sm:w-[350px] sm:h-[350px] md:w-[450px] md:h-[450px] lg:w-[600px] lg:h-[600px] flex items-center justify-center">
      {/* Deep background glow */}
      <motion.div
        className="absolute w-[200px] h-[200px] sm:w-[250px] sm:h-[250px] md:w-[300px] md:h-[300px] lg:w-[400px] lg:h-[400px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(123,46,255,0.4) 0%, rgba(88,28,235,0.2) 40%, transparent 70%)',
          filter: 'blur(40px)',
        }}
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.6, 1, 0.6],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Secondary cyan glow */}
      <motion.div
        className="absolute w-[180px] h-[180px] sm:w-[230px] sm:h-[230px] md:w-[280px] md:h-[280px] lg:w-[380px] lg:h-[380px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(0,212,255,0.3) 0%, transparent 60%)',
          filter: 'blur(30px)',
        }}
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.4, 0.7, 0.4],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.5,
        }}
      />

      {/* Main orb container with rotation */}
      <motion.div
        className="relative z-10"
        animate={{
          rotate: [0, 360],
        }}
        transition={{
          duration: 80,
          repeat: Infinity,
          ease: 'linear',
        }}
      >
        {/* The orb image */}
        <motion.img
          src="https://customer-assets.emergentagent.com/job_webk-digital/artifacts/uo6cjvns_IMG_0985.jpeg"
          alt="Energy Orb"
          className="w-[220px] h-[220px] sm:w-[280px] sm:h-[280px] md:w-[350px] md:h-[350px] lg:w-[500px] lg:h-[500px] object-contain"
          style={{
            filter: 'brightness(1.1) contrast(1.05)',
            maskImage: 'radial-gradient(circle at 50% 50%, black 40%, transparent 52%)',
            WebkitMaskImage: 'radial-gradient(circle at 50% 50%, black 40%, transparent 52%)',
          }}
          animate={{
            scale: [1, 1.03, 1],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </motion.div>

      {/* Floating energy particles */}
      {[...Array(25)].map((_, i) => {
        const angle = (i / 25) * Math.PI * 2;
        const radius = 180 + Math.random() * 60;
        const startX = Math.cos(angle) * radius;
        const startY = Math.sin(angle) * radius;
        
        return (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: Math.random() * 4 + 2 + 'px',
              height: Math.random() * 4 + 2 + 'px',
              background: i % 3 === 0 ? '#7B2EFF' : i % 3 === 1 ? '#00D4FF' : '#A855F7',
              boxShadow: `0 0 ${Math.random() * 15 + 8}px ${i % 3 === 0 ? 'rgba(123,46,255,0.8)' : i % 3 === 1 ? 'rgba(0,212,255,0.8)' : 'rgba(168,85,247,0.8)'}`,
              left: '50%',
              top: '50%',
            }}
            animate={{
              x: [startX, startX + (Math.random() - 0.5) * 40, startX],
              y: [startY, startY + (Math.random() - 0.5) * 40, startY],
              opacity: [0.3, 1, 0.3],
              scale: [0.8, 1.3, 0.8],
            }}
            transition={{
              duration: 3 + Math.random() * 4,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: Math.random() * 3,
            }}
          />
        );
      })}

      {/* Outer energy ring */}
      <motion.div
        className="absolute w-[250px] h-[250px] sm:w-[310px] sm:h-[310px] md:w-[380px] md:h-[380px] lg:w-[530px] lg:h-[530px] rounded-full border border-[#7B2EFF]/30"
        style={{
          boxShadow: '0 0 30px rgba(123,46,255,0.2), inset 0 0 30px rgba(123,46,255,0.1)',
        }}
        animate={{
          rotate: [0, 360],
          scale: [1, 1.02, 1],
        }}
        transition={{
          rotate: { duration: 25, repeat: Infinity, ease: 'linear' },
          scale: { duration: 4, repeat: Infinity, ease: 'easeInOut' },
        }}
      />

      {/* Inner energy ring */}
      <motion.div
        className="absolute w-[210px] h-[210px] sm:w-[260px] sm:h-[260px] md:w-[320px] md:h-[320px] lg:w-[470px] lg:h-[470px] rounded-full border border-[#00D4FF]/20"
        style={{
          boxShadow: '0 0 20px rgba(0,212,255,0.15)',
        }}
        animate={{
          rotate: [360, 0],
        }}
        transition={{
          duration: 35,
          repeat: Infinity,
          ease: 'linear',
        }}
      />
    </div>
  );
};

const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0a0a12 0%, #050508 50%, #0a0510 100%)',
      }}
      data-testid="hero-section"
    >
      {/* Ambient background glows */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 right-1/3 w-[600px] h-[600px] bg-[#7B2EFF]/8 rounded-full blur-[180px]" />
        <div className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] bg-[#00D4FF]/5 rounded-full blur-[150px]" />
      </div>

      {/* Subtle grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(123, 46, 255, 0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(123, 46, 255, 0.5) 1px, transparent 1px)
          `,
          backgroundSize: '100px 100px',
        }}
      />

      {/* Main content */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 relative z-10 w-full">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-4 items-center">
          {/* Left - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left order-2 lg:order-1"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#7B2EFF]/15 to-[#00D4FF]/15 border border-[#7B2EFF]/25 mb-8"
            >
              <motion.span 
                className="w-2 h-2 rounded-full bg-gradient-to-r from-[#7B2EFF] to-[#00D4FF]"
                animate={{ 
                  scale: [1, 1.4, 1],
                  opacity: [0.7, 1, 0.7],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span className="text-xs uppercase tracking-[0.2em] text-white/70 font-medium">Web Development Studio</span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] tracking-tight"
            >
              Build the{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7B2EFF] via-[#A855F7] to-[#00D4FF]">
                Future
              </span>
              <br />
              of Your Brand
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-4 sm:mt-6 text-base sm:text-lg md:text-xl text-white/55 max-w-xl mx-auto lg:mx-0 leading-relaxed"
            >
              We craft premium digital experiences with cutting-edge technology, stunning design, and blazing-fast performance.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-6 sm:mt-8 md:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start"
            >
              <motion.button
                onClick={() => scrollToSection('#contact')}
                className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#7B2EFF] to-[#5B1FCC] rounded-xl text-white font-semibold overflow-hidden shadow-lg shadow-[#7B2EFF]/25 text-sm sm:text-base"
                whileHover={{ 
                  scale: 1.03, 
                  boxShadow: '0 0 50px rgba(123,46,255,0.5)' 
                }}
                whileTap={{ scale: 0.98 }}
                data-testid="primary-cta-button"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Start a Project
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </motion.button>
              <motion.button
                onClick={() => scrollToSection('#portfolio')}
                className="px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-white font-semibold border border-white/15 backdrop-blur-sm text-sm sm:text-base"
                whileHover={{ 
                  borderColor: 'rgba(123,46,255,0.5)', 
                  backgroundColor: 'rgba(123,46,255,0.08)' 
                }}
                whileTap={{ scale: 0.98 }}
                data-testid="secondary-cta-button"
              >
                View Our Work
              </motion.button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-8 sm:mt-10 md:mt-14 flex items-center gap-6 sm:gap-8 md:gap-10 justify-center lg:justify-start flex-wrap"
            >
              {[
                { value: '35+', label: 'Projects' },
                { value: '98%', label: 'Satisfaction' },
                { value: '1+', label: 'Year' },
              ].map((stat, index) => (
                <div key={index} className="text-center" data-testid={`hero-stat-${index}`}>
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                  <div className="text-xs text-white/40 uppercase tracking-wider mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right - Energy Orb */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="relative flex items-center justify-center order-1 lg:order-2"
          >
            <EnergyOrb />
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 rounded-full border-2 border-white/15 flex items-start justify-center p-2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1.5 h-2 bg-gradient-to-b from-[#7B2EFF] to-[#00D4FF] rounded-full"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
